'use es6';

import colr from './vendor/colr';
import PropTypes from 'prop-types';
import { Component } from 'react';
import styled from 'foundations-theming/utils';
import { MERCURY_LAYER, VENUS_LAYER, VENUS_TROPOSPHERE_LAYER } from 'foundations-components/transitional/constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const BoardContainer = styled.div.withConfig({
  displayName: "Board__BoardContainer",
  componentId: "sc-1jhwfq2-0"
})(["position:relative;height:100%;min-height:146px;min-width:146px;user-select:none;& > img{height:100%;}"]);
const BoardInner = styled.div.withConfig({
  displayName: "Board__BoardInner",
  componentId: "sc-1jhwfq2-1"
})(["position:absolute;top:0;right:0;bottom:0;left:0;"]);
const BoardPanel = styled.div.withConfig({
  displayName: "Board__BoardPanel",
  componentId: "sc-1jhwfq2-2"
})(({
  theme,
  $backgroundColor
}) => ({
  width: '100%',
  height: '100%',
  position: 'relative',
  zIndex: MERCURY_LAYER,
  borderRadius: theme.borderRadius['100'],
  backgroundColor: $backgroundColor
}));
const BoardPanelValue = styled.div.withConfig({
  displayName: "Board__BoardPanelValue",
  componentId: "sc-1jhwfq2-3"
})(({
  theme
}) => ({
  borderRadius: theme.borderRadius['100'],
  position: 'absolute',
  width: '100%',
  height: '100%',
  left: 0,
  top: 0,
  zIndex: VENUS_LAYER,
  backgroundImage: 'linear-gradient(to bottom, transparent 0%, #000000 100%)'
}));
const BoardPanelSaturation = styled.div.withConfig({
  displayName: "Board__BoardPanelSaturation",
  componentId: "sc-1jhwfq2-4"
})(({
  theme
}) => ({
  borderRadius: theme.borderRadius['100'],
  position: 'absolute',
  width: '100%',
  height: '100%',
  left: 0,
  top: 0,
  zIndex: MERCURY_LAYER,
  backgroundImage: 'linear-gradient(to right, #ffffff 0%, transparent 100%)'
}));
const BoardPanelIndicator = styled.span.withConfig({
  displayName: "Board__BoardPanelIndicator",
  componentId: "sc-1jhwfq2-5"
})(({
  theme,
  $left,
  $top
}) => ({
  position: 'absolute',
  borderRadius: '10px',
  border: `${theme.borderWidth['100']} solid ${theme.color['border-core-on-fill-default']}`,
  width: '9px',
  height: '9px',
  left: $left || '-999px',
  top: $top || '-999px',
  boxShadow: '0 0 1px rgba(120, 120, 120, 0.7)',
  zIndex: VENUS_LAYER
}));
const BoardHandler = styled.div.withConfig({
  displayName: "Board__BoardHandler",
  componentId: "sc-1jhwfq2-6"
})(({
  theme
}) => ({
  boxShadow: '0 0 2px #808080 inset',
  borderRadius: theme.borderRadius['100'],
  cursor: 'crosshair',
  userSelect: 'none',
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  zIndex: VENUS_TROPOSPHERE_LAYER
}));
export default class Board extends Component {
  constructor(...args) {
    super(...args);
    this.onBoardMouseDown = evt => {
      const x = evt.clientX;
      const y = evt.clientY;
      this.pointMoveTo({
        x,
        y
      });
      window.addEventListener('mousemove', this.onBoardDrag);
      window.addEventListener('mouseup', this.onBoardDragEnd);
    };
    this.onBoardDrag = evt => {
      const x = evt.clientX;
      const y = evt.clientY;
      this.pointMoveTo({
        x,
        y
      });
    };
    this.onBoardDragEnd = evt => {
      const x = evt.clientX;
      const y = evt.clientY;
      this.pointMoveTo({
        x,
        y
      });
      this.removeMouseListeners();
    };
    this.pointMoveTo = pos => {
      const rect = this.measureBounds();
      const width = rect.width;
      const height = rect.height;
      let left = pos.x - rect.left;
      let top = pos.y - rect.top;
      left = Math.max(0, left);
      left = Math.min(left, width);
      top = Math.max(0, top);
      top = Math.min(top, height);
      const hsv = {
        h: this.props.hue,
        s: left / width * 100,
        v: (1 - top / height) * 100
      };
      const hex = colr.fromHsvObject(hsv).toHex();
      this.props.onChange({
        hex
      });
    };
  }
  componentWillUnmount() {
    this.removeMouseListeners();
  }
  removeMouseListeners() {
    window.removeEventListener('mousemove', this.onBoardDrag);
    window.removeEventListener('mouseup', this.onBoardDragEnd);
  }
  measureBounds() {
    return this.board && this.board.getBoundingClientRect();
  }
  render() {
    const {
      hex,
      hue
    } = this.props;
    const {
      s,
      v
    } = colr.fromHex(hex).toHsvObject();
    const x = `calc(${s}% - 4px)`;
    const y = `calc(${100 - v}% - 4px)`;
    const hueHsv = [hue, 100, 100];
    const backgroundColor = colr.fromHsvArray(hueHsv).toHex();
    return /*#__PURE__*/_jsxs(BoardContainer, {
      ref: el => this.board = el,
      "data-test-id": "board-container",
      children: [/*#__PURE__*/_jsx("img", {
        src: "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
      }), /*#__PURE__*/_jsxs(BoardInner, {
        children: [/*#__PURE__*/_jsxs(BoardPanel, {
          "data-test-id": "board-inner-panel",
          $backgroundColor: backgroundColor,
          children: [/*#__PURE__*/_jsx(BoardPanelValue, {}), /*#__PURE__*/_jsx(BoardPanelSaturation, {})]
        }), /*#__PURE__*/_jsx(BoardPanelIndicator, {
          "data-test-id": "board-slider-indicator",
          $left: x,
          $top: y
        }), /*#__PURE__*/_jsx(BoardHandler, {
          "data-test-id": "board-handler",
          onMouseDown: this.onBoardMouseDown
        })]
      })]
    });
  }
}
Board.propTypes = {
  hex: PropTypes.string,
  hue: PropTypes.number,
  onChange: PropTypes.func
};